/*
 * Highland & Hive product availability display and Shopify Forms integration.
 * Shopify Forms handles the signup, double opt-in and CAPTCHA.
 * Our code supplies the selected fragrance/size (or Discovery trio).
 */
if (!customElements.get('product-waitlist')) {
  customElements.define('product-waitlist', class ProductWaitlist extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.formApp = this.productInfo?.querySelector('[data-hh-waitlist-app]');
      this.unavailableMessage = this.querySelector('[data-waitlist-unavailable]');
      this.variantLabel = this.querySelector('[data-waitlist-variant]');
      this.purchaseControls = this.productInfo?.querySelectorAll('[data-purchase-controls]') || [];
      this.quantityControls = this.productInfo?.querySelector('[data-quantity-controls]');
      this.productTitle = this.dataset.productTitle || 'This product';
      this.hasOnlyDefaultVariant = this.dataset.hasOnlyDefaultVariant === 'true';
      this.isDiscovery = this.dataset.discoveryWaitlist === 'true';
      this.discoverySelectionIds = [];
      this.discoverySelectionComplete = false;
      this.discoveryCombinationAvailable = false;
      this.formReady = false;
      this.seenForms ||= new WeakSet();
      // Only compensate for viewport movement caused by Shopify Forms' success transition.
      // Never fight intentional scrolling after a submission.
      this.pendingSubmitScroll = null;
      this.pendingSignup = null;
      this.restoringSubmitScroll = null;
      this.handleIntentionalScroll = (event) => {
        if (event.type === 'keydown' && !['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) return;
        if (this.pendingSubmitScroll) this.pendingSubmitScroll.userScrolled = true;
        if (this.restoringSubmitScroll) this.restoringSubmitScroll.userScrolled = true;
      };
      window.addEventListener('wheel', this.handleIntentionalScroll, { passive: true });
      window.addEventListener('touchmove', this.handleIntentionalScroll, { passive: true });
      window.addEventListener('keydown', this.handleIntentionalScroll);
      this.currentVariant = this.initialVariant;
      this.sync(this.currentVariant);

      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (String(data.sectionId) !== this.dataset.sectionId) return;
        this.sync(data.variant);
      });

      // Shopify Forms mounts asynchronously into an open shadow root.
      // Poll only for initial readiness, then observe its document for changes.
      this.mountAttempts = 0;
      this.mountTimer = window.setInterval(() => {
        if (this.prepareForm()) {
          window.clearInterval(this.mountTimer);
          this.mountTimer = null;
          return;
        }
        this.mountAttempts += 1;
        // Offer a contact route after 8.4 seconds while Forms keeps loading.
        if (this.mountAttempts >= 24 && this.unavailableMessage) {
          this.unavailableMessage.hidden = false;
        }
        if (this.mountAttempts >= 120) {
          window.clearInterval(this.mountTimer);
          this.mountTimer = null;
        }
      }, 350);
      this.prepareForm();
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      if (this.mountTimer) window.clearInterval(this.mountTimer);
      this.mountTimer = null;
      this.formObserver?.disconnect();
      this.formObserver = null;
      this.formRoot = null;
      window.removeEventListener('wheel', this.handleIntentionalScroll);
      window.removeEventListener('touchmove', this.handleIntentionalScroll);
      window.removeEventListener('keydown', this.handleIntentionalScroll);
    }

    get initialVariant() {
      try {
        return JSON.parse(this.querySelector('[data-initial-variant]')?.textContent || 'null');
      } catch {
        return null;
      }
    }

    sync(variant) {
      this.currentVariant = variant;
      const hasVariant = Boolean(variant?.id);
      const available = hasVariant && Boolean(variant.available);
      const title = this.hasOnlyDefaultVariant
        ? this.productTitle
        : this.productTitle + ', ' + (variant?.title || 'Selected size');

      if (this.isDiscovery) {
        this.syncDiscoveryVisibility();
      } else {
        this.hidden = !hasVariant || available;
        this.purchaseControls.forEach(control => { control.hidden = !available; });
        if (this.quantityControls) this.quantityControls.hidden = !available;
      }
      if (this.variantLabel) this.variantLabel.textContent = title;
      this.updateForm();
    }

    // Called by the existing Discovery selector whenever the trio changes.
    setDiscoverySelection(choices, combinationAvailable) {
      if (!this.isDiscovery) return;
      const ids = choices.map(choice => String(choice?.dataset?.variantId || ''))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
      this.discoverySelectionIds = ids;
      this.discoverySelectionComplete = ids.length === 3 && new Set(ids).size === 3;
      this.discoveryCombinationAvailable = Boolean(combinationAvailable);
      this.syncDiscoveryVisibility();
    }

    syncDiscoveryVisibility() {
      if (!this.isDiscovery) return;
      const canPurchase = Boolean(
        this.currentVariant?.available &&
        this.discoverySelectionComplete &&
        this.discoveryCombinationAvailable
      );
      this.hidden = !this.discoverySelectionComplete || canPurchase;
      this.purchaseControls.forEach(control => { control.hidden = !canPurchase; });
      if (this.quantityControls) this.quantityControls.hidden = !canPurchase;
      this.updateForm();
    }

    selectedFragrances() {
      const choices = [...(this.productInfo?.querySelectorAll('discovery-selector [data-discovery-choice]:checked') || [])];
      return choices.map(choice => ({
        id: String(choice.dataset.variantId || ''),
        name: choice.closest('.discovery-selector__option')
          ?.querySelector('.discovery-selector__name')?.textContent?.trim() || 'Unknown fragrance'
      })).sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }));
    }

    selectedPreference() {
      if (this.isDiscovery) {
        if (!this.discoverySelectionComplete) return '';
        const selected = this.selectedFragrances();
        if (selected.length !== 3) return '';
        if (selected.some(item => !item.id) || new Set(selected.map(item => item.id)).size !== 3) return '';
        return 'Discovery Collection | ' +
          selected.map(item => item.name + ' [3 oz Mini #' + item.id + ']').join(' + ');
      }
      if (!this.currentVariant?.id || this.currentVariant.available) return '';
      return this.productTitle + ' | ' + this.currentVariant.title + ' | ' + this.currentVariant.id;
    }

    isNumberedFragrance(name) {
      return typeof name === 'string' && /^No\. (?:0[1-9]|[1-9]\d+)$/.test(name);
    }

    sortFragranceNumbers(names) {
      // Canonical decimal labels sort by length, then digits, without Number precision limits.
      return [...names].sort((a, b) => a.length - b.length || a.localeCompare(b));
    }

    signupContext() {
      const variant = this.currentVariant;
      if (!/^[1-9]\d*$/.test(String(variant?.id || ''))) {
        throw new Error('Waitlist selection has no Shopify variant');
      }
      if (!this.isDiscovery) {
        const source = this.productInfo?.querySelector('variant-selects [data-selected-variant]');
        const selectedVariant = source ? JSON.parse(source.textContent) : this.initialVariant;
        if (!this.isNumberedFragrance(this.productTitle) || variant.available !== false ||
            !['3 oz Mini', '10 oz Signature'].includes(variant.title) ||
            String(selectedVariant?.id) !== String(variant.id) || selectedVariant?.title !== variant.title) {
          throw new Error('Waitlist selection does not match the Shopify fragrance variant');
        }
        return {
          waitlistType: 'fragrance', productName: this.productTitle,
          size: variant.title, fragrances: [this.productTitle]
        };
      }

      const selected = this.selectedFragrances();
      const source = this.productInfo?.querySelector('[data-discovery-bundle]');
      const bundle = JSON.parse(source?.textContent || 'null');
      if (!this.discoverySelectionComplete || selected.length !== 3 ||
          new Set(selected.map(item => item.id)).size !== 3 ||
          new Set(selected.map(item => item.name)).size !== 3 ||
          !selected.every(item => this.isNumberedFragrance(item.name) &&
            this.discoverySelectionIds.includes(item.id)) ||
          String(bundle?.variantId) !== String(variant.id) || !Array.isArray(bundle?.components)) {
        throw new Error('Waitlist selection does not match the Shopify Discovery trio');
      }
      const handles = selected.map(item => {
        const components = bundle.components.filter(component => String(component.variant_id) === item.id);
        if (!components.length || components.some(component =>
          component.variant_title !== '3 oz Mini' || Number(component.quantity_in_bundle) !== 1 ||
          component.product_title !== item.name || typeof component.handle !== 'string' || !component.handle ||
          component.handle !== components[0].handle
        )) {
          throw new Error('Waitlist fragrance is not a configured Shopify Mini component');
        }
        return components[0].handle;
      });
      if (new Set(handles).size !== 3) throw new Error('Waitlist Discovery products must be distinct');
      return {
        waitlistType: 'discovery', productName: this.productTitle,
        size: '3 oz Mini', fragrances: selected.map(item => item.name)
      };
    }

    updateForm() {
      if (!this.formApp || !this.formReady) return;
      this.formApp.hidden = this.hidden || !this.selectedPreference();
      this.prepareFormFields();
    }

    prepareForm() {
      const root = this.formApp?.querySelector('shopify-forms-embed')?.shadowRoot;
      if (!root) return false;
      this.formRoot = root;
      const field = root.querySelector('input[id="custom#requested_selection"]');
      if (!field) return false;
      this.formReady = true;
      if (this.unavailableMessage) this.unavailableMessage.hidden = true;
      this.prepareFormFields();
      this.formApp.classList?.remove('product-waitlist__form-pending');
      this.updateFormVisibility();
      if (!this.formObserver && typeof MutationObserver !== 'undefined') {
        this.formObserver = new MutationObserver(() => {
          this.prepareFormFields();
          this.restoreScrollAfterConfirmation();
        });
        this.formObserver.observe(root, { childList: true, subtree: true });
      }
      return true;
    }

    updateFormVisibility() {
      if (!this.formApp || !this.formReady) return;
      this.formApp.hidden = this.hidden || !this.selectedPreference();
    }

    prepareFormFields() {
      const root = this.formRoot;
      if (!root) return;
      const field = root.querySelector('input[id="custom#requested_selection"]');
      // The field disappears after Shopify Forms shows its success message.
      if (field) {
        field.parentElement.hidden = true;
        field.parentElement.style.display = 'none';
        const preference = this.selectedPreference();
        if (preference && field.value !== preference) {
          const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
          if (setter) setter.call(field, preference);
          else field.value = preference;
          field.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
          field.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
        }
      }

      root.querySelectorAll('h1, h2, h3, h4, p').forEach(element => {
        const label = element.textContent?.trim();
        if (label === 'Notify me' || label === "We'll email you when your selection becomes available.") {
          element.hidden = true;
          element.style.display = 'none';
        }
      });

      // Preserve vertical space during inline success to avoid mobile scroll jump.
      const form = root.querySelector('form');
      if (form && !this.seenForms.has(form)) {
        this.seenForms.add(form);
        form.addEventListener('submit', () => {
          const height = Math.ceil(this.formApp?.getBoundingClientRect().height || 0);
          if (height > 0) this.formApp.style.minHeight = height + 'px';
          // Capture context now; Shopify Forms removes inputs on success.
          const preference = this.selectedPreference();
          this.pendingSignup = preference ? {
            submittedAt: Date.now(),
            context: null,
          } : null;
          if (this.pendingSignup) {
            try {
              // Snapshot trusted Shopify context; later selection changes must not rewrite this request.
              this.pendingSignup.context = this.signupContext();
            } catch (error) {
              console.error('[HH waitlist] Invalid signup analytics context', error);
            }
          }
          this.pendingSubmitScroll = {
            top: window.scrollY,
            left: window.scrollX,
            submittedAt: Date.now(),
            userScrolled: false
          };
        }, { capture: true });
      }
    }

    publishSuccessfulSignup() {
      const signup = this.pendingSignup;
      if (!signup) return;
      this.pendingSignup = null;
      // Count only successful Shopify Forms requests, never button clicks or
      // validation attempts. This is not email double-opt-in completion.
      if (Date.now() - signup.submittedAt > 120000) return;
      const context = signup.context;
      if (!context) return;
      const data = {
        waitlist_type: context.waitlistType,
        product_name: context.productName,
        selected_size: context.size,
        fragrance_count: context.fragrances.length,
        fragrance_numbers: context.fragrances.join(','),
      };
      try {
        const analytics = window.Shopify?.analytics;
        if (!analytics?.publish) throw new Error('Shopify customer events are unavailable');
        Promise.resolve(analytics.publish('hh:waitlist_signup', data))
          .then(accepted => {
            if (accepted === false) console.warn('[HH waitlist] Shopify did not accept the analytics event');
          })
          .catch(error => console.error('[HH waitlist] Unable to publish analytics event', error));
      } catch (error) {
        console.error('[HH waitlist] Unable to publish analytics event', error);
      }
      this.forwardSuccessfulSignup(data, context);
    }

    forwardSuccessfulSignup(data, context) {
      try {
        const privacy = window.Shopify?.customerPrivacy;
        if (typeof privacy?.analyticsProcessingAllowed !== 'function') {
          throw new Error('Shopify customer privacy API is unavailable');
        }
        // Analytics permission is separate from sale/sharing. The official tag
        // retains its advertising consent and restricted-data-processing settings.
        if (privacy.analyticsProcessingAllowed() !== true) return;
        if (typeof window.gtag !== 'function') throw new Error('Official Google tag is unavailable');

        context = context || this.signupContext();
        const count = data?.waitlist_type === 'discovery' ? 3 : data?.waitlist_type === 'fragrance' ? 1 : 0;
        const fragrances = typeof data?.fragrance_numbers === 'string' ? data.fragrance_numbers.split(',') : [];
        if (!count || data.fragrance_count !== count || fragrances.length !== count ||
            new Set(fragrances).size !== count || !fragrances.every(number => this.isNumberedFragrance(number)) ||
            data.waitlist_type !== context.waitlistType || data.product_name !== context.productName ||
            data.selected_size !== context.size ||
            this.sortFragranceNumbers(fragrances).join(',') !== this.sortFragranceNumbers(context.fragrances).join(',')) {
          throw new Error('Invalid waitlist analytics metadata');
        }
        const location = new URL(window.location.href);
        if (location.hostname !== 'highlandhive.co' || !/^\/products\/[a-z0-9-]+$/.test(location.pathname)) {
          throw new Error('Waitlist analytics event is not from a Highland & Hive product page');
        }
        const referrer = document.referrer ? new URL(document.referrer) : null;
        // Reuse the official tag and its session. A second tag in a custom
        // pixel also generated automatic scroll events during live testing.
        window.gtag('event', 'waitlist_signup', {
          send_to: 'G-R13C02E08N',
          waitlist_type: data.waitlist_type,
          product_name: data.product_name,
          selected_size: data.selected_size,
          fragrance_count: count,
          fragrance_numbers: this.sortFragranceNumbers(fragrances).join(','),
          page_location: location.origin + location.pathname,
          page_title: data.product_name,
          page_referrer: referrer && ['https:', 'http:'].includes(referrer.protocol) ? referrer.origin : '',
          ...(location.searchParams.get('hh_ga4_debug') === '1' ? { debug_mode: true } : {}),
        });
      } catch (error) {
        console.error('[HH waitlist] GA4 forwarding failed', error);
      }
    }

    restoreScrollAfterConfirmation() {
      const pending = this.pendingSubmitScroll;
      if (!pending || !this.formRoot) return;
      // Shopify Forms removes its custom field when replacing the form with
      // its double-opt-in confirmation. Do not restore on validation errors.
      if (this.formRoot.querySelector('input[id="custom#requested_selection"]')) return;
      const confirmed = [...this.formRoot.querySelectorAll('h1, h2, h3, h4')]
        .some(element => element.textContent?.trim() === 'Check your inbox');
      if (!confirmed) return;

      this.publishSuccessfulSignup();
      this.pendingSubmitScroll = null;
      if (pending.userScrolled || Date.now() - pending.submittedAt > 12000) return;
      this.restoringSubmitScroll = pending;
      // A single transition can involve both a React render and focus/scroll
      // adjustments. Restore after layout, without making subsequent scrolling sticky.
      const restore = () => {
        if (this.restoringSubmitScroll !== pending || pending.userScrolled) {
          if (this.restoringSubmitScroll === pending) this.restoringSubmitScroll = null;
          return;
        }
        if (Math.abs(window.scrollY - pending.top) > 1) {
          window.scrollTo({ top: pending.top, left: pending.left, behavior: 'instant' });
        }
      };
      window.requestAnimationFrame(() => {
        restore();
        window.requestAnimationFrame(() => {
          restore();
          if (this.restoringSubmitScroll === pending) this.restoringSubmitScroll = null;
        });
      });
    }
  });
}
